import React, { useMemo } from 'react';
import { Card, Button, Box, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import { getProductPath, isMikdashProduct } from '../utils/productSlug';

export default function HeroSection({ products = [] }) {
    const { language, isHebrew } = useLanguage();
    const t = translations[language];

    const mikdashProduct = useMemo(
        () => (Array.isArray(products) ? products.find((p) => isMikdashProduct(p)) : null),
        [products]
    );

    const mikdashPath = mikdashProduct
        ? getProductPath(mikdashProduct, products)
        : '/';

    const mikdashCtaLabel = (() => {
        if (!mikdashProduct) {
            return isHebrew ? 'ערכת המקדש' : 'The Second Mikdash';
        }
        const name = isHebrew
            ? (mikdashProduct.name_he || mikdashProduct.name_en)
            : (mikdashProduct.name_en || mikdashProduct.name_he);
        const trimmed = name != null ? String(name).trim() : '';
        return trimmed || (isHebrew ? 'ערכת המקדש' : 'The Second Mikdash');
    })();

    return (
        <Card
            elevation={0}
            sx={{
                height: { xs: 'calc(84vh - 85px)', sm: '84vh' },
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '15px',
                boxShadow: 'none',
                /* No top margin — gap under the header comes only from --page-header-offset */
                margin: '0 0 15px',
                backgroundColor: 'transparent',
                opacity: 1
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '15px',
                    backgroundImage: 'url(/hero_section.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    zIndex: 0
                }}
            />

            {/* Miluim Image - Centered on mobile, left with rotation on desktop */}
            <Box
                sx={{
                    position: 'absolute',
                    top: { xs: '9%', md: '8%' },
                    left: { xs: '50%', md: '5%' },
                    transform: {
                        xs: 'translateX(-50%) rotate(0deg)',
                        md: 'translateX(0) rotate(-12deg)'
                    },
                    zIndex: 10,
                    backgroundColor: 'rgba(229, 90, 61, 1)',
                    border: '1px solid rgba(245, 240, 227, 1)',
                    borderRadius: 1,
                    boxShadow: '0 8px 32px rgba(229, 90, 61, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    transformOrigin: 'center center',
                }}
            >
                <Box
                    component="img"
                    src="/miluim.jpeg"
                    alt="Miluim"
                    sx={{
                        width: { xs: '180px', sm: '220px', md: '400px', lg: '400px' },
                        height: 'auto',
                        borderRadius: 0,
                        display: 'block',
                        objectFit: 'contain'
                    }}
                />
            </Box>

            {/* Hero tagline — desktop + mobile */}
            <Typography
                sx={{
                    position: 'absolute',
                    top: { xs: '61%', md: '68%' },
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    color: 'rgba(245, 240, 227, 1)',
                    fontSize: { xs: '1.2rem', sm: '1.2rem', md: '1.4rem', lg: '1.6rem' },
                    fontWeight: 500,
                    letterSpacing: '0.02em',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    direction: isHebrew ? 'rtl' : 'ltr',
                    pointerEvents: 'none',
                    textShadow: '0 2px 12px rgba(0,0,0,0.35)',
                    px: 2
                }}
            >
                {t.heroTagline}
            </Typography>

            {/* Button Overlay - Positioned in lower quarter */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '10%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10
                }}
            >
                <Link to={mikdashPath} style={{ textDecoration: 'none' }}>
                    <Button
                        variant="contained"
                        startIcon={isHebrew ? <ArrowBackIcon /> : null}
                        endIcon={!isHebrew ? <ArrowForwardIcon /> : null}
                        sx={{
                            backgroundColor: 'rgba(229, 90, 61, 1)',
                            border: '1px solid rgba(245, 240, 227, 1)',
                            color: 'rgba(245, 240, 227, 1)',
                            px: { xs: 3, md: 4 },
                            py: { xs: 1.5, md: 2 },
                            fontSize: { xs: '1.1rem', md: '1.4rem', lg: '1.5rem' },
                            fontWeight: 600,
                            borderRadius: 3,
                            boxShadow: '0 8px 32px rgba(229, 90, 61, 0.4)',
                            transition: 'all 0.3s ease',
                            minWidth: { xs: '200px', md: '250px' },
                            maxWidth: { xs: 'min(92vw, 340px)', md: '420px' },
                            '& .MuiButton-label, &': {
                                whiteSpace: 'normal',
                                lineHeight: 1.25,
                                textAlign: 'center'
                            },
                            '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                                '& > *:nth-of-type(1)': {
                                    fontSize: { xs: '1.25rem', md: '1.5rem' }
                                }
                            },
                            '&:hover': {
                                backgroundColor: 'rgba(245, 240, 227, 1)',
                                color: 'rgba(229, 90, 61, 1)',
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 40px rgba(229, 90, 61, 0.5)',
                            }
                        }}
                    >
                        {mikdashCtaLabel}
                    </Button>
                </Link>
            </Box>
        </Card>
    );
}
